import React, { useCallback, useEffect, useState } from 'react';
import { SCATTER_PLOT_CHART_CONTEXT } from '@/components/Graphs/constants';
import { MaGraph } from '@/components/Graphs/MaGraph';
import { getGraphLayout } from '@/components/Graphs/MaGraph/helpers';
import {
  LOCAL_STORAGE_KEY_ACQUISITION_COST,
  LOCAL_STORAGE_KEY_PRICE_BARREL,
} from '@/utils/constants';
import { useGetMinMaxFilters } from '../Filters/hooks/useGetMinMaxFilters';
import { MA_DEALS_FIELDS_ENUM } from './constants';
import { useGetChartData } from './hooks/useGetChartData';
import { useStyles } from './styles';

const setLocalStorage = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

const CommonGraph = ({ localStorageKey, minMaxLoading, data, fields, title, altYAxisTitle }) => {
  const [minMaxFilter, setMinMaxFilter] = useState({
    [data.min.key]: data?.min?.initialValue || data?.min?.default,
    [data.max.key]: data?.max?.initialValue || data?.max?.default,
  });

  const setMinMaxFilterPersist = useCallback(
    value => {
      setMinMaxFilter(value);
      setLocalStorage(localStorageKey, value);
    },
    [localStorageKey],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Update minMaxFilter when loading stops
  useEffect(() => {
    if (!minMaxLoading) {
      setMinMaxFilter({
        [data.min.key]: data?.min?.initialValue || data?.min?.default,
        [data.max.key]: data?.max?.initialValue || data?.max?.default,
      });
    }
  }, [minMaxLoading]);

  const minMaxConfig = {
    values: minMaxFilter || {},
    setValues: setMinMaxFilterPersist,
    options: data,
    loading: minMaxLoading,
  };

  const { chartData, loading, error } = useGetChartData(minMaxFilter || {}, fields);
  const graphLayout = getGraphLayout({
    showLegend: true,
    yAxisTitle: title,
    xAxisType: 'date',
    altYAxisTitle,
  });

  if (error) return null;

  return (
    <MaGraph
      data={chartData}
      title={title}
      loading={loading}
      graphLayout={graphLayout}
      minMaxConfig={minMaxConfig}
      graphContext={SCATTER_PLOT_CHART_CONTEXT}
    />
  );
};

export const TransactionAnalytics = () => {
  const classes = useStyles();
  const { acquisitionCost, priceFlowingBarrel, loading: minMaxLoading } = useGetMinMaxFilters();

  return (
    <div className={classes.graphWrapper}>
      <CommonGraph
        data={acquisitionCost}
        title='Acquisition Cost (US$/boe)'
        altYAxisTitle='Consideration per Reserves (US$/boe)'
        minMaxLoading={minMaxLoading}
        localStorageKey={LOCAL_STORAGE_KEY_ACQUISITION_COST}
        fields={[
          MA_DEALS_FIELDS_ENUM.ACQUISITION_COST,
          MA_DEALS_FIELDS_ENUM.CONSIDERATION_BY_RESERVES,
        ]}
      />
      <CommonGraph
        data={priceFlowingBarrel}
        title='Price per Flowing Barrel (US$/boe/d)'
        altYAxisTitle='Consideration per Production (US$/boe/d)'
        minMaxLoading={minMaxLoading}
        localStorageKey={LOCAL_STORAGE_KEY_PRICE_BARREL}
        fields={[
          MA_DEALS_FIELDS_ENUM.PRICE_FLOWING_BARREL,
          MA_DEALS_FIELDS_ENUM.CONSIDERATION_BY_PRODUCTION,
        ]}
      />
    </div>
  );
};
